import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import api from "../../lib/api";
import PageHeader from "../../components/PageHeader";
import { Button } from "../../components/ui/button";
import CrudModal from "../../components/CrudModal";
import { toast } from "sonner";
import { Briefcase, Buildings, TrendUp, GraduationCap, PencilSimple, Trash, Plus } from "@phosphor-icons/react";

const FIELDS = [
  { name: "name", label: "Recruiter name", type: "text" },
  { name: "offers", label: "# Offers", type: "number" },
  { name: "ctc", label: "Median CTC (LPA)", type: "number" },
];

export default function Placements() {
  const { t } = useTranslation();
  const [s, setS] = useState(null);
  const [modal, setModal] = useState({ open: false, editing: null });

  const load = useCallback(async () => {
    const { data } = await api.get("/placements/summary");
    setS(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => setModal({ open: true, editing: null });
  const openEdit = (r) => setModal({ open: true, editing: r });
  const close = () => setModal({ open: false, editing: null });

  const save = async (values) => {
    try {
      if (modal.editing) {
        await api.patch(`/placements/recruiters/${modal.editing.recruiter_id}`, values);
        toast.success("Recruiter updated");
      } else {
        await api.post("/placements/recruiters", values);
        toast.success("Recruiter added");
      }
      close(); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Remove ${r.name} from recruiter directory?`)) return;
    try { await api.delete(`/placements/recruiters/${r.recruiter_id}`); toast.success("Removed"); load(); }
    catch { toast.error("Failed to delete"); }
  };

  if (!s) return <div className="p-10 text-sm text-muted-foreground">Loading…</div>;

  return (
    <div>
      <PageHeader
        overline="M12 · Outcomes"
        title={t("modules.placements.title")}
        description={t("modules.placements.subtitle")}
        action={
          <Button onClick={openCreate} className="rounded-md bg-[hsl(244_53%_20%)] hover:bg-[hsl(244_53%_15%)]" data-testid="new-recruiter-btn">
            <Plus size={14} className="mr-1.5"/> Add recruiter
          </Button>
        }
      />
      <div className="p-6 lg:p-8 space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border rounded-md overflow-hidden">
          {[
            { I: Briefcase, l: "Offers", v: s.offers, t: "this cycle" },
            { I: GraduationCap, l: "Students placed", v: s.students_placed, t: "across 8 departments" },
            { I: TrendUp, l: "Avg. package", v: `₹${s.avg_package_lpa} LPA`, t: "offer-weighted" },
            { I: TrendUp, l: "Highest", v: `₹${s.highest_lpa} LPA`, t: "top offer" },
          ].map(k => (
            <div key={k.l} className="bg-white p-5">
              <k.I size={20} weight="duotone" className="text-[hsl(244_53%_20%)]"/>
              <div className="eos-overline mt-3">{k.l}</div>
              <div className="font-heading font-extrabold text-3xl mt-1 tracking-tight">{k.v}</div>
              <div className="text-[11px] text-muted-foreground mt-1">{k.t}</div>
            </div>
          ))}
        </div>

        <div className="eos-card p-0 overflow-hidden">
          <div className="p-4 border-b border-border flex items-center gap-2"><Buildings size={16} weight="duotone"/><div className="font-heading font-semibold text-base">Recruiter directory</div></div>
          <table className="w-full text-sm">
            <thead className="bg-secondary text-muted-foreground">
              <tr>{["Recruiter", "Offers", "Median CTC", "Trend", "Actions"].map(h => (
                <th key={h} className="text-left px-4 py-2.5 text-[11px] uppercase tracking-[0.15em] font-medium">{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {s.recruiters.map(r => (
                <tr key={r.recruiter_id} className="border-t border-border hover:bg-secondary/40" data-testid={`recruiter-${r.recruiter_id}`}>
                  <td className="px-4 py-3 font-medium">{r.name}</td>
                  <td className="px-4 py-3 font-mono-eos">{r.offers}</td>
                  <td className="px-4 py-3 font-mono-eos">₹{r.ctc} LPA</td>
                  <td className="px-4 py-3">
                    <div className="h-1.5 w-32 bg-border rounded-full overflow-hidden">
                      <div className="h-full bg-[hsl(20_90%_48%)]" style={{ width: `${Math.min(100, (r.offers / 90) * 100)}%` }}/>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" onClick={() => openEdit(r)} className="h-7 w-7 p-0 rounded" title="Edit" data-testid={`edit-${r.recruiter_id}`}><PencilSimple size={13}/></Button>
                      <Button variant="ghost" onClick={() => remove(r)} className="h-7 w-7 p-0 rounded text-rose-600 hover:text-rose-700 hover:bg-rose-50" title="Delete" data-testid={`delete-${r.recruiter_id}`}><Trash size={13}/></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <CrudModal
        open={modal.open}
        onClose={close}
        title={modal.editing ? "Edit recruiter" : "Add recruiter"}
        fields={FIELDS}
        initial={modal.editing || { name: "", offers: 0, ctc: 0 }}
        onSubmit={save}
        submitLabel={modal.editing ? "Save changes" : "Add recruiter"}
      />
    </div>
  );
}
